import React, { useMemo, useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Alert,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { useSelectedSchool } from '../contexts/SelectedSchoolContext';
import FilterSection, { type FilterSectionState } from '../components/FilterSection';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import type { DispersalListItem, MessageState, StudentOption, TimelineStep, DispersalsResponse, EarlyListResponse } from '../types';
import { authService } from '@uniqcamp/shared';
import { useParentStudents } from '../api/students';

const today = () => new Date().toISOString().slice(0, 10);

/** Status column: timeline (Early dispersal requested → Class teacher → Principal → Cancelled/Rejected if applicable) */
function EarlyRequestStatusText({ d, currentUserId }: { d: DispersalListItem; currentUserId?: string | null }) {
  const status = d.status;
  const teacherName = d.approvedBy?.teacher?.name;
  const principalName = d.approvedBy?.principal?.name;
  const step1Done = true;
  const step2Done = !!teacherName || (status !== 'PENDING' && status !== 'CANCELLED' && status !== 'REJECTED');
  const step3Done = !!principalName || status === 'APPROVED_BY_PRINCIPAL' || status === 'COMPLETED';

  let steps: TimelineStep[] = [
    { label: 'Early dispersal requested', done: step1Done },
    {
      label: step2Done ? `Approved by class teacher${teacherName ? ` (${teacherName})` : ''}` : 'Waiting for class teacher to approve',
      done: step2Done,
    },
    {
      label: step3Done ? `Approved by principal${principalName ? ` (${principalName})` : ''}` : 'Waiting for principal to approve',
      done: step3Done,
    },
  ];

  if (status === 'CANCELLED') {
    const by = d.cancelledBy?.name;
    const isYou = currentUserId && d.cancelledBy?._id && currentUserId === d.cancelledBy._id;
    const byLabel = isYou ? 'you' : by || '–';
    const cancelComment = d.cancellationComment?.trim();
    steps = [
      ...steps,
      { label: `Cancelled (by ${byLabel})`, subLabel: cancelComment || undefined, done: true, variant: 'cancelled' },
    ];
  } else if (status === 'REJECTED') {
    const by = d.rejectedBy?.name;
    const reason = d.rejectionReason?.trim();
    steps = [
      ...steps,
      { label: by ? `Rejected (by ${by})` : 'Rejected', subLabel: reason || undefined, done: true, variant: 'rejected' },
    ];
  }

  const getDotSx = (step: TimelineStep) => {
    if (step.variant === 'cancelled') {
      return { bgcolor: 'text.secondary', border: '2px solid', borderColor: 'text.secondary', boxShadow: 'none' };
    }
    if (step.variant === 'rejected') {
      return { bgcolor: 'error.main', border: '2px solid', borderColor: 'error.dark', boxShadow: '0 0 0 2px rgba(211, 47, 47, 0.2)' };
    }
    if (step.done) {
      return { bgcolor: 'success.main', border: '2px solid', borderColor: 'success.dark', boxShadow: '0 0 0 2px rgba(46, 125, 50, 0.2)' };
    }
    return { bgcolor: 'warning.main', border: '2px solid', borderColor: 'warning.dark', boxShadow: '0 0 0 2px rgba(237, 108, 2, 0.2)' };
  };

  const getLabelColor = (step: TimelineStep) => {
    if (step.variant === 'cancelled') return 'text.secondary';
    if (step.variant === 'rejected') return 'error.main';
    return step.done ? 'success.dark' : 'text.secondary';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5, position: 'relative', pl: 1 }}>
      <Box
        sx={{
          position: 'absolute',
          left: 12,
          top: 10,
          bottom: 10,
          width: 2,
          bgcolor: 'action.selected',
          borderRadius: 1,
        }}
      />
      {steps.map((step, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              flexShrink: 0,
              width: 10,
              height: 10,
              borderRadius: '50%',
              mt: 0.6,
              ...getDotSx(step),
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: step.done ? 600 : 500, color: getLabelColor(step) }}
            >
              {step.label}
            </Typography>
            {step.subLabel && (
              <Tooltip title={step.subLabel} placement="top" arrow>
                <CommentOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: getLabelColor(step),
                    verticalAlign: 'middle',
                    cursor: 'help',
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

const earlyRequestColumns: DataTableColumn<DispersalListItem>[] = [
  {
    id: 'date',
    label: 'Date',
    render: (d) => d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '–',
  },
  {
    id: 'student',
    label: 'Student',
    render: (d) => d.studentId?.name ?? '–',
  },
  {
    id: 'class',
    label: 'Class & section',
    render: (d) => (d.studentId ? `${d.studentId.class ?? ''}${d.studentId.section ? ` ${d.studentId.section}` : ''}`.trim() || '–' : '–'),
  },
  {
    id: 'pickupTime',
    label: 'Pickup time',
    render: (d) => (d.dispersalTime || d.dismissalTime) ? new Date(d.dispersalTime || d.dismissalTime!).toLocaleString() : '–',
  },
  {
    id: 'status',
    label: 'Status',
    minWidth: 200,
    render: (d) => <EarlyRequestStatusText d={d} currentUserId={(authService.getStoredUser() as { id?: string } | null)?.id} />,
  },
  {
    id: 'notes',
    label: 'Notes',
    minWidth: 140,
    render: (d) => (d.notes ? String(d.notes).slice(0, 80) + (String(d.notes).length > 80 ? '…' : '') : '–'),
  },
];

const CANCELLABLE_STATUSES = ['PENDING'];

const DispersalEarly: React.FC = () => {
  const queryClient = useQueryClient();
  const { selectedSchoolId } = useSelectedSchool();
  const [message, setMessage] = useState<MessageState>({ type: '', text: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState(today());
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
  const [pickupTime, setPickupTime] = useState(''); // time only (HH:mm), date comes from dialogDate
  const [notes, setNotes] = useState('');
  const [listPage, setListPage] = useState(0);
  const [listRowsPerPage, setListRowsPerPage] = useState(10);
  const [listFilters, setListFilters] = useState<FilterSectionState>({
    dateFrom: '',
    dateTo: '',
    status: '',
    search: '',
  });
  const [cancelConfirmDispersal, setCancelConfirmDispersal] = useState<DispersalListItem | null>(null);
  const [cancelComment, setCancelComment] = useState('');

  const cancelMutation = useMutation(
    (payload: { dispersalId: string; comment?: string }) =>
      apiClient.put(`/dispersals/${payload.dispersalId}/cancel`, { comment: payload.comment || '' }).then((r: { data: unknown }) => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['dispersals']);
        queryClient.invalidateQueries(['dispersals', 'early-list']);
        setMessage({ type: 'success', text: 'Request cancelled.' });
        setCancelConfirmDispersal(null);
        setCancelComment('');
      },
      onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
        const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to cancel request.';
        setMessage({ type: 'error', text: msg });
      },
    }
  );

  const todayStr = today();
  const { data: studentsData } = useParentStudents(selectedSchoolId || undefined, {
    enabled: dialogOpen && Boolean(selectedSchoolId),
  });
  const students: StudentOption[] = useMemo(
    () => (studentsData?.students ?? []).filter((s) => s.viewerCanDispersal !== false),
    [studentsData],
  );

  const { data: dialogDispersalsData } = useQuery<DispersalsResponse>(
    selectedSchoolId && dialogOpen ? ['dispersals', dialogDate, selectedSchoolId] : ['dispersals', dialogDate],
    () =>
      apiClient
        .get(selectedSchoolId ? `/dispersals?date=${dialogDate}&schoolId=${selectedSchoolId}` : `/dispersals?date=${dialogDate}`)
        .then((r: { data: unknown }) => r.data as DispersalsResponse),
    { enabled: dialogOpen }
  );
  const dialogDispersals: DispersalListItem[] = dialogDispersalsData?.dispersals ?? [];
  const earlyForDialogDate = dialogDispersals.filter((d) => d.requestType === 'EARLY_DISPERSAL' || d.requestType === 'EARLY_DISMISSAL');
  const activeEarlyForDialogDate = earlyForDialogDate.filter((d) => d.status !== 'CANCELLED' && d.status !== 'REJECTED');

  const listQueryParams = new URLSearchParams();
  listQueryParams.set('requestType', 'EARLY_DISPERSAL');
  listQueryParams.set('page', String(listPage + 1));
  listQueryParams.set('limit', String(listRowsPerPage));
  if (selectedSchoolId) listQueryParams.set('schoolId', selectedSchoolId);
  if (listFilters.dateFrom) listQueryParams.set('dateFrom', listFilters.dateFrom);
  if (listFilters.dateTo) listQueryParams.set('dateTo', listFilters.dateTo);
  if (listFilters.status) listQueryParams.set('status', listFilters.status);
  if (listFilters.search?.trim()) listQueryParams.set('q', listFilters.search.trim());
  const { data: earlyListData, isLoading: earlyListLoading } = useQuery<EarlyListResponse>(
    ['dispersals', 'early-list', selectedSchoolId, listPage, listRowsPerPage, listFilters.dateFrom, listFilters.dateTo, listFilters.status, listFilters.search],
    () => apiClient.get(`/dispersals?${listQueryParams.toString()}`).then((r: { data: unknown }) => r.data as EarlyListResponse),
    { enabled: !!selectedSchoolId }
  );
  const allEarlyDispersals = earlyListData?.dispersals ?? [];
  const totalRows = earlyListData?.total ?? 0;

  const earlyMutation = useMutation(
    async (payload: { studentIds: string[]; dispersalTime: string; notes: string }) => {
      const time = payload.dispersalTime || new Date().toISOString();
      for (const studentId of payload.studentIds) {
        await apiClient.post('/dispersals/early', {
          studentId,
          dispersalTime: time,
          notes: payload.notes,
          ...(selectedSchoolId && { schoolId: selectedSchoolId }),
        });
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['dispersals']);
        queryClient.invalidateQueries(['dispersals', todayStr]);
        queryClient.invalidateQueries(['dispersals', dialogDate]);
        queryClient.invalidateQueries(['dispersals', 'early-all']);
        queryClient.invalidateQueries(['dispersals', 'early-list']);
        if (selectedSchoolId) {
          queryClient.invalidateQueries(['dispersals', todayStr, selectedSchoolId]);
          queryClient.invalidateQueries(['dispersals', dialogDate, selectedSchoolId]);
          queryClient.invalidateQueries(['dispersals', 'early-all', selectedSchoolId]);
        }
        setSelectedStudentIds(new Set());
        setPickupTime('');
        setNotes('');
        setDialogOpen(false);
        setMessage({ type: 'success', text: 'Early dispersal requested. Class teacher and principal will approve.' });
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } }; message?: string };
        setMessage({ type: 'error', text: e.response?.data?.message ?? e.message ?? 'Error' });
      },
    }
  );

  const toggleStudent = (studentId: string, checked: boolean) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(studentId);
      else next.delete(studentId);
      return next;
    });
  };

  const openDialog = () => {
    setDialogDate(todayStr);
    setSelectedStudentIds(new Set());
    setPickupTime('');
    setNotes('');
    setMessage({ type: '', text: '' });
    setDialogOpen(true);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight={600}>
            Early Request
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Request early dispersal for a date. After approval, use Dispersal Request at the gate from the time shown.
          </Typography>
        </Box>
        {selectedSchoolId && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openDialog}>
            Make early Request
          </Button>
        )}
      </Box>

      {!selectedSchoolId && (
        <Alert severity="info" sx={{ mb: 2 }}>Please select a school first to make an early request.</Alert>
      )}

      {message.text && (
        <Alert
          severity={message.type === 'error' ? 'error' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Make early Request</DialogTitle>
        <DialogContent>
          {!selectedSchoolId ? (
            <Alert severity="info">Select a school first.</Alert>
          ) : (
            <>
              <TextField
                size="small"
                type="date"
                label="Request date"
                value={dialogDate}
                onChange={(e) => setDialogDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Select students (one request per student per date).
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2, maxHeight: 280, overflow: 'auto' }}>
                {students.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No students for this school.</Typography>
                ) : (
                  students.map((student) => {
                    const alreadyRequestedForDate = activeEarlyForDialogDate.some((d) => d.studentId?._id === student._id);
                    const isSelected = selectedStudentIds.has(student._id);
                    return (
                      <Card key={student._id} variant="outlined" sx={{ borderRadius: 1 }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => toggleStudent(student._id, e.target.checked)}
                              disabled={alreadyRequestedForDate}
                              size="small"
                            />
                            <PersonIcon color="action" fontSize="small" />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>{student.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Class {student.class ?? ''}{student.section ? ` ${student.section}` : ''}
                                {alreadyRequestedForDate && ' · Already requested for this date'}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Box>
              {selectedStudentIds.size > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Request for {selectedStudentIds.size} student(s)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="time"
                    label="Requested pickup time"
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Notes (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={2}
                  />
                </Paper>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setMessage({ type: '', text: '' });
              const fullDatetime = pickupTime
                ? new Date(`${dialogDate}T${pickupTime}`).toISOString()
                : new Date().toISOString();
              earlyMutation.mutate({
                studentIds: Array.from(selectedStudentIds),
                dispersalTime: fullDatetime,
                notes,
              });
            }}
            disabled={selectedStudentIds.size === 0 || earlyMutation.isLoading}
          >
            {earlyMutation.isLoading ? 'Submitting…' : 'Submit request'}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedSchoolId && (
        <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
          <FilterSection
            filters={listFilters}
            onFilterChange={(partial) => {
              setListFilters((prev) => ({ ...prev, ...partial }));
              setListPage(0);
            }}
            onClear={() => {
              setListFilters({ dateFrom: '', dateTo: '', status: '', search: '' });
              setListPage(0);
            }}
            showDateRange
            showStatus
            showSearch
            searchLabel="Search"
            searchPlaceholder="Search by student name or notes…"
          />

          <DataTable<DispersalListItem>
            columns={[
              ...earlyRequestColumns,
              {
                id: 'actions',
                label: 'Actions',
                align: 'right' as const,
                minWidth: 100,
                render: (d) => {
                  const canCancel = CANCELLABLE_STATUSES.includes(d.status);
                  const isCancelling = cancelMutation.isLoading && (cancelMutation.variables as { dispersalId?: string } | undefined)?.dispersalId === d._id;
                  if (!canCancel) return '–';
                  return (
                    <Tooltip title="Cancel this early request">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Cancel request"
                        onClick={() => { setCancelConfirmDispersal(d); setCancelComment(''); }}
                        disabled={isCancelling}
                      >
                        <CancelOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  );
                },
              },
            ]}
            rows={allEarlyDispersals}
            rowKey="_id"
            pagination={{
              page: listPage,
              rowsPerPage: listRowsPerPage,
              total: totalRows,
              onPageChange: setListPage,
              onRowsPerPageChange: (v) => { setListRowsPerPage(v); setListPage(0); },
              rowsPerPageOptions: [5, 10, 25, 50],
            }}
            emptyMessage="No early requests found."
            loading={earlyListLoading}
            getRowSx={(d) => (d.createdAt && d.createdAt.slice(0, 10) === todayStr ? { bgcolor: 'action.hover' } : undefined)}
          />
        </Paper>
      )}

      <Dialog
        open={!!cancelConfirmDispersal}
        onClose={() => !cancelMutation.isLoading && (setCancelConfirmDispersal(null), setCancelComment(''))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel early request?</DialogTitle>
        <DialogContent>
          {cancelConfirmDispersal && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Are you sure you want to cancel the early request for{' '}
                <strong>{cancelConfirmDispersal.studentId?.name ?? 'this student'}</strong>?
                This cannot be undone.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Comment (optional)"
                placeholder="Reason or note – visible to school"
                value={cancelComment}
                onChange={(e) => setCancelComment(e.target.value)}
                size="small"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setCancelConfirmDispersal(null); setCancelComment(''); }}
            disabled={cancelMutation.isLoading}
          >
            Keep request
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => cancelConfirmDispersal && cancelMutation.mutate({ dispersalId: cancelConfirmDispersal._id, comment: cancelComment })}
            disabled={cancelMutation.isLoading}
          >
            {cancelMutation.isLoading ? 'Cancelling…' : 'Yes, cancel request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DispersalEarly;
